<script setup>
import { Link } from '@inertiajs/vue3';
import { onMounted, ref } from "vue";
import { PerfectScrollbar } from 'vue3-perfect-scrollbar';

const props = defineProps({
    user: Object,
});

let menu, animate, isHorizontalLayout = false;
const TRANSITION_EVENTS = ['transitionend', 'webkitTransitionEnd', 'oTransitionEnd'];
const DELTA = 5;
const TRANS_EVENTS = ['transitionend', 'webkitTransitionEnd', 'oTransitionEnd'];
const TRANS_PROPERTIES = ['transition', 'MozTransition', 'webkitTransition', 'WebkitTransition', 'OTransition'];
const INLINE_STYLES = `
.layout-menu-fixed .layout-navbar-full .layout-menu,
.layout-menu-fixed-offcanvas .layout-navbar-full .layout-menu {
  top: {navbarHeight}px !important;
}
.layout-page {
  padding-top: {navbarHeight}px !important;
}
.content-wrapper {
  padding-bottom: {footerHeight}px !important;
}`;

// Guard
function requiredParam(name) {
    throw new Error(`Parameter required${name ? `: \`${name}\`` : ''}`)
}

const Helpers = {
    // Root Element
    ROOT_EL: typeof window !== 'undefined' ? document.documentElement : null,

    // Large screens breakpoint
    LAYOUT_BREAKPOINT: 1200,

    // Resize delay in milliseconds
    RESIZE_DELAY: 200,

    menuPsScroll: null,

    mainMenu: null,

    // Internal variables
    _curStyle: null,
    _styleEl: null,
    _resizeTimeout: null,
    _resizeCallback: null,
    _transitionCallback: null,
    _transitionCallbackTimeout: null,
    _listeners: [],
    _initialized: false,
    _autoUpdate: false,
    _lastWindowHeight: 0,

    // *******************************************************************************
    // * Utilities

    // ---
    // Scroll To Active Menu Item
    _scrollToActive(animate = false, duration = 500) {
        const layoutMenu = this.getLayoutMenu()

        if (!layoutMenu) return

        let activeEl = layoutMenu.querySelector('li.menu-item.active:not(.open)')

        if (activeEl) {
            // t = current time
            // b = start value
            // c = change in value
            // d = duration
            const easeInOutQuad = (t, b, c, d) => {
                t /= d / 2
                if (t < 1) return (c / 2) * t * t + b
                t -= 1
                return (-c / 2) * (t * (t - 2) - 1) + b
            }

            const element = this.getLayoutMenu().querySelector('.menu-inner')

            if (typeof activeEl === 'string') {
                activeEl = document.querySelector(activeEl)
            }
            if (typeof activeEl !== 'number') {
                activeEl = activeEl.getBoundingClientRect().top + element.scrollTop
            }

            // If active element's top position is less than 2/3 (66%) of menu height than do not scroll
            if (activeEl < parseInt((element.clientHeight * 2) / 3, 10)) return

            const start = element.scrollTop
            const change = activeEl - start - parseInt(element.clientHeight / 2, 10)
            const startDate = +new Date()

            if (animate === true) {
                const animateScroll = () => {
                    const currentDate = +new Date()
                    const currentTime = currentDate - startDate
                    const val = easeInOutQuad(currentTime, start, change, duration)
                    element.scrollTop = val
                    if (currentTime < duration) {
                        requestAnimationFrame(animateScroll)
                    } else {
                        element.scrollTop = change
                    }
                }
                animateScroll()
            } else {
                element.scrollTop = change
            }
        }
    },

    // ---
    // Swipe In Gesture
    _swipeIn(targetEl, callback) {
        const { Hammer } = window
        if (typeof Hammer !== 'undefined' && typeof targetEl === 'string') {
            // Swipe menu gesture
            const swipeInElement = document.querySelector(targetEl)

            if (swipeInElement) {
                const hammerInstance = new Hammer(swipeInElement)

                hammerInstance.on('panright', callback)
            }
        }
    },

    // ---
    // Swipe Out Gesture
    _swipeOut(targetEl, callback) {
        const { Hammer } = window
        if (typeof Hammer !== 'undefined' && typeof targetEl === 'string') {
            setTimeout(() => {
                // Swipe menu gesture
                const swipeOutElement = document.querySelector(targetEl)

                if (swipeOutElement) {
                    const hammerInstance = new Hammer(swipeOutElement)

                    hammerInstance.get('pan').set({ direction: Hammer.DIRECTION_ALL, threshold: 250 })
                    hammerInstance.on('panleft', callback)
                }
            }, 500)
        }
    },

    // ---
    // Swipe Out On Overlay Tap
    _overlayTap(targetEl, callback) {
        const { Hammer } = window

        if (typeof Hammer !== 'undefined' && typeof targetEl === 'string') {
            // Swipe out overlay element
            const swipeOutOverlayElement = document.querySelector(targetEl)

            if (swipeOutOverlayElement) {
                const hammerInstance = new Hammer(swipeOutOverlayElement)

                hammerInstance.on('tap', callback)
            }
        }
    },

    // ---
    // Add classes
    _addClass(cls, el = this.ROOT_EL) {
        if (el.length !== undefined) {
            // Add classes to multiple elements
            el.forEach(e => {
                cls.split(' ').forEach(c => e.classList.add(c))
            })
        } else {
            // Add classes to single element
            cls.split(' ').forEach(c => el.classList.add(c))
        }
    },

    // ---
    // Remove classes
    _removeClass(cls, el = this.ROOT_EL) {
        if (el.length !== undefined) {
            // Remove classes to multiple elements
            el.forEach(e => {
                cls.split(' ').forEach(c => e.classList.remove(c))
            })
        } else {
            // Remove classes to single element
            cls.split(' ').forEach(c => el.classList.remove(c))
        }
    },

    // Toggle classes
    _toggleClass(el = this.ROOT_EL, cls1, cls2) {
        if (el.classList.contains(cls1)) {
            el.classList.replace(cls1, cls2)
        } else {
            el.classList.replace(cls2, cls1)
        }
    },

    // ---
    // Has class
    _hasClass(cls, el = this.ROOT_EL) {
        let result = false

        cls.split(' ').forEach(c => {
            if (el.classList.contains(c)) result = true
        })

        return result
    },

    _findParent(el, cls) {
        if ((el && el.tagName.toUpperCase() === 'BODY') || el.tagName.toUpperCase() === 'HTML') return null
        el = el.parentNode
        while (el && el.tagName.toUpperCase() !== 'BODY' && !el.classList.contains(cls)) {
            el = el.parentNode
        }
        el = el && el.tagName.toUpperCase() !== 'BODY' ? el : null
        return el
    },

    // ---
    // Trigger window event
    _triggerWindowEvent(name) {
        if (typeof window === 'undefined') return

        if (document.createEvent) {
            let event

            if (typeof Event === 'function') {
                event = new Event(name)
            } else {
                event = document.createEvent('Event')
                event.initEvent(name, false, true)
            }

            window.dispatchEvent(event)
        } else {
            window.fireEvent(`on${name}`, document.createEventObject())
        }
    },

    // ---
    // Trigger event
    _triggerEvent(name) {
        this._triggerWindowEvent(`layout${name}`)

        this._listeners.filter(listener => listener.event === name).forEach(listener => listener.callback.call(null))
    },

    // ---
    // Update style
    _updateInlineStyle(navbarHeight = 0, footerHeight = 0) {
        if (!this._styleEl) {
            this._styleEl = document.createElement('style')
            this._styleEl.type = 'text/css'
            document.head.appendChild(this._styleEl)
        }

        const newStyle = INLINE_STYLES.replace(/\{navbarHeight\}/gi, navbarHeight).replace(
            /\{footerHeight\}/gi,
            footerHeight
        )

        if (this._curStyle !== newStyle) {
            this._curStyle = newStyle
            this._styleEl.textContent = newStyle
        }
    },

    // ---
    // Remove style
    _removeInlineStyle() {
        if (this._styleEl) document.head.removeChild(this._styleEl)
        this._styleEl = null
        this._curStyle = null
    },

    // ---
    // Redraw layout menu (Safari bugfix)
    _redrawLayoutMenu() {
        const layoutMenu = this.getLayoutMenu()

        if (layoutMenu && layoutMenu.querySelector('.menu')) {
            const inner = layoutMenu.querySelector('.menu-inner')
            const { scrollTop } = inner
            const pageScrollTop = document.documentElement.scrollTop

            layoutMenu.style.display = 'none'
            // layoutMenu.offsetHeight
            layoutMenu.style.display = ''
            inner.scrollTop = scrollTop
            document.documentElement.scrollTop = pageScrollTop

            return true
        }

        return false
    },

    // ---
    // Check for transition support
    _supportsTransitionEnd() {
        if (window.QUnit) return false

        const el = document.body || document.documentElement

        if (!el) return false

        let result = false
        TRANS_PROPERTIES.forEach(evnt => {
            if (typeof el.style[evnt] !== 'undefined') result = true
        })

        return result
    },

    // ---
    // Calculate current navbar height
    _getNavbarHeight() {
        const layoutNavbar = this.getLayoutNavbar()

        if (!layoutNavbar) return 0
        if (!this.isSmallScreen()) return layoutNavbar.getBoundingClientRect().height

        // Needs some logic to get navbar height on small screens

        const clonedEl = layoutNavbar.cloneNode(true)
        clonedEl.id = null
        clonedEl.style.visibility = 'hidden'
        clonedEl.style.position = 'absolute'

        Array.prototype.slice.call(clonedEl.querySelectorAll('.collapse.show')).forEach(el => this._removeClass('show', el))

        layoutNavbar.parentNode.insertBefore(clonedEl, layoutNavbar)

        const navbarHeight = clonedEl.getBoundingClientRect().height

        clonedEl.parentNode.removeChild(clonedEl)

        return navbarHeight
    },

    // ---
    // Get current footer height
    _getFooterHeight() {
        const layoutFooter = this.getLayoutFooter()

        if (!layoutFooter) return 0

        return layoutFooter.getBoundingClientRect().height
    },

    // ---
    // Get animation duration of element
    _getAnimationDuration(el) {
        const duration = window.getComputedStyle(el).transitionDuration

        return parseFloat(duration) * (duration.indexOf('ms') !== -1 ? 1 : 1000)
    },

    // ---
    // Set menu hover state
    _setMenuHoverState(hovered) {
        this[hovered ? '_addClass' : '_removeClass']('layout-menu-hover')
    },

    // ---
    // Toggle collapsed
    _setCollapsed(collapsed) {
        if (this.isSmallScreen()) {
            if (collapsed) {
                this._removeClass('layout-menu-expanded')
            } else {
                setTimeout(
                    () => {
                        this._addClass('layout-menu-expanded')
                    },
                    this._redrawLayoutMenu() ? 5 : 0
                )
            }
        } else {
            this[collapsed ? '_addClass' : '_removeClass']('layout-menu-collapsed')
        }
    },

    // ---
    // Add layout sivenav toggle animationEnd event
    _bindLayoutAnimationEndEvent(modifier, cb) {
        const menu = this.getMenu()
        const duration = menu ? this._getAnimationDuration(menu) + 50 : 0

        if (!duration) {
            modifier.call(this)
            cb.call(this)
            return
        }

        this._transitionCallback = e => {
            if (e.target !== menu) return
            this._unbindLayoutAnimationEndEvent()
            cb.call(this)
        }

        TRANS_EVENTS.forEach(e => {
            menu.addEventListener(e, this._transitionCallback, false)
        })

        modifier.call(this)

        this._transitionCallbackTimeout = setTimeout(() => {
            this._transitionCallback.call(this, { target: menu })
        }, duration)
    },

    // ---
    // Remove layout sivenav toggle animationEnd event
    _unbindLayoutAnimationEndEvent() {
        const menu = this.getMenu()

        if (this._transitionCallbackTimeout) {
            clearTimeout(this._transitionCallbackTimeout)
            this._transitionCallbackTimeout = null
        }

        if (menu && this._transitionCallback) {
            TRANS_EVENTS.forEach(e => {
                menu.removeEventListener(e, this._transitionCallback, false)
            })
        }

        if (this._transitionCallback) {
            this._transitionCallback = null
        }
    },

    // ---
    // Bind delayed window resize event
    _bindWindowResizeEvent() {
        this._unbindWindowResizeEvent()

        const cb = () => {
            if (this._resizeTimeout) {
                clearTimeout(this._resizeTimeout)
                this._resizeTimeout = null
            }
            this._triggerEvent('resize')
        }

        this._resizeCallback = () => {
            if (this._resizeTimeout) clearTimeout(this._resizeTimeout)
            this._resizeTimeout = setTimeout(cb, this.RESIZE_DELAY)
        }

        window.addEventListener('resize', this._resizeCallback, false)
    },

    // ---
    // Unbind delayed window resize event
    _unbindWindowResizeEvent() {
        if (this._resizeTimeout) {
            clearTimeout(this._resizeTimeout)
            this._resizeTimeout = null
        }

        if (this._resizeCallback) {
            window.removeEventListener('resize', this._resizeCallback, false)
            this._resizeCallback = null
        }
    },

    _bindMenuMouseEvents() {
        if (this._menuMouseEnter && this._menuMouseLeave && this._windowTouchStart) return

        const layoutMenu = this.getLayoutMenu()
        if (!layoutMenu) return this._unbindMenuMouseEvents()

        if (!this._menuMouseEnter) {
            this._menuMouseEnter = () => {
                if (
                    this.isSmallScreen() ||
                    !this._hasClass('layout-menu-collapsed') ||
                    this.isOffcanvas() ||
                    this._hasClass('layout-transitioning')
                ) {
                    return this._setMenuHoverState(false)
                }

                return this._setMenuHoverState(true)
            }
            layoutMenu.addEventListener('mouseenter', this._menuMouseEnter, false)
            layoutMenu.addEventListener('touchstart', this._menuMouseEnter, false)
        }

        if (!this._menuMouseLeave) {
            this._menuMouseLeave = () => {
                this._setMenuHoverState(false)
            }
            layoutMenu.addEventListener('mouseleave', this._menuMouseLeave, false)
        }

        if (!this._windowTouchStart) {
            this._windowTouchStart = e => {
                if (!e || !e.target || !this._findParent(e.target, '.layout-menu')) {
                    this._setMenuHoverState(false)
                }
            }
            window.addEventListener('touchstart', this._windowTouchStart, true)
        }
    },

    _unbindMenuMouseEvents() {
        if (!this._menuMouseEnter && !this._menuMouseLeave && !this._windowTouchStart) return

        const layoutMenu = this.getLayoutMenu()

        if (this._menuMouseEnter) {
            if (layoutMenu) {
                layoutMenu.removeEventListener('mouseenter', this._menuMouseEnter, false)
                layoutMenu.removeEventListener('touchstart', this._menuMouseEnter, false)
            }
            this._menuMouseEnter = null
        }

        if (this._menuMouseLeave) {
            if (layoutMenu) {
                layoutMenu.removeEventListener('mouseleave', this._menuMouseLeave, false)
            }
            this._menuMouseLeave = null
        }

        if (this._windowTouchStart) {
            if (layoutMenu) {
                window.addEventListener('touchstart', this._windowTouchStart, true)
            }
            this._windowTouchStart = null
        }

        this._setMenuHoverState(false)
    },

    // *******************************************************************************
    // * Methods

    scrollToActive(animate = false) {
        this._scrollToActive(animate)
    },

    swipeIn(el, callback) {
        this._swipeIn(el, callback)
    },

    swipeOut(el, callback) {
        this._swipeOut(el, callback)
    },

    overlayTap(el, callback) {
        this._overlayTap(el, callback)
    },

    scrollPageTo(to, duration = 500) {
        // t = current time
        // b = start value
        // c = change in value
        // d = duration
        const easeInOutQuad = (t, b, c, d) => {
            t /= d / 2
            if (t < 1) return (c / 2) * t * t + b
            t -= 1
            return (-c / 2) * (t * (t - 2) - 1) + b
        }

        const element = document.scrollingElement

        if (typeof to === 'string') {
            to = document.querySelector(to)
        }
        if (typeof to !== 'number') {
            to = to.getBoundingClientRect().top + element.scrollTop
        }

        const start = element.scrollTop
        const change = to - start
        const startDate = +new Date()
        // const increment = 20

        const animateScroll = () => {
            const currentDate = +new Date()
            const currentTime = currentDate - startDate
            const val = easeInOutQuad(currentTime, start, change, duration)
            element.scrollTop = val
            if (currentTime < duration) {
                requestAnimationFrame(animateScroll)
            } else {
                element.scrollTop = to
            }
        }
        animateScroll()
    },

    // ---
    // Collapse / expand layout
    setCollapsed(collapsed = requiredParam('collapsed'), animate = true) {
        const layoutMenu = this.getLayoutMenu()

        if (!layoutMenu) return

        this._unbindLayoutAnimationEndEvent()

        if (animate && this._supportsTransitionEnd()) {
            this._addClass('layout-transitioning')
            if (collapsed) this._setMenuHoverState(false)

            this._bindLayoutAnimationEndEvent(
                () => {
                    // Collapse / Expand
                    this._setCollapsed(collapsed)
                },
                () => {
                    this._removeClass('layout-transitioning')
                    this._triggerWindowEvent('resize')
                    this._triggerEvent('toggle')
                    this._setMenuHoverState(false)
                }
            )
        } else {
            this._addClass('layout-no-transition')
            if (collapsed) this._setMenuHoverState(false)

            // Collapse / Expand
            this._setCollapsed(collapsed)

            setTimeout(() => {
                this._removeClass('layout-no-transition')
                this._triggerWindowEvent('resize')
                this._triggerEvent('toggle')
                this._setMenuHoverState(false)
            }, 1)
        }
    },

    // ---
    // Toggle layout
    toggleCollapsed(animate = true) {
        this.setCollapsed(!this.isCollapsed(), animate)
    },

    // ---
    // Set layout positioning
    setPosition(fixed = requiredParam('fixed'), offcanvas = requiredParam('offcanvas')) {
        this._removeClass('layout-menu-offcanvas layout-menu-fixed layout-menu-fixed-offcanvas')

        if (!fixed && offcanvas) {
            this._addClass('layout-menu-offcanvas')
        } else if (fixed && !offcanvas) {
            this._addClass('layout-menu-fixed')
            this._redrawLayoutMenu()
        } else if (fixed && offcanvas) {
            this._addClass('layout-menu-fixed-offcanvas')
            this._redrawLayoutMenu()
        }

        this.update()
    },

    // *******************************************************************************
    // * Getters

    getLayoutMenu() {
        return document.querySelector('.layout-menu')
    },

    getMenu() {
        const layoutMenu = this.getLayoutMenu()

        if (!layoutMenu) return null

        return !this._hasClass('menu', layoutMenu) ? layoutMenu.querySelector('.menu') : layoutMenu
    },

    getLayoutNavbar() {
        return document.querySelector('.layout-navbar')
    },

    getLayoutFooter() {
        return document.querySelector('.content-footer')
    },

    getLayoutContainer() {
        return document.querySelector('.layout-page')
    },

    // *******************************************************************************
    // * Setters

    setNavbarFixed(fixed = requiredParam('fixed')) {
        this[fixed ? '_addClass' : '_removeClass']('layout-navbar-fixed')
        this.update()
    },

    setFooterFixed(fixed = requiredParam('fixed')) {
        this[fixed ? '_addClass' : '_removeClass']('layout-footer-fixed')
        this.update()
    },

    setFlipped(reversed = requiredParam('reversed')) {
        this[reversed ? '_addClass' : '_removeClass']('layout-menu-flipped')
    },

    // *******************************************************************************
    // * Update

    update() {
        if (
            (this.getLayoutNavbar() &&
                ((!this.isSmallScreen() && this.isLayoutNavbarFull() && this.isFixed()) || this.isNavbarFixed())) ||
            (this.getLayoutFooter() && this.isFooterFixed())
        ) {
            this._updateInlineStyle(this._getNavbarHeight(), this._getFooterHeight())
        }

        this._bindMenuMouseEvents()
    },

    setAutoUpdate(enable = requiredParam('enable')) {
        if (enable && !this._autoUpdate) {
            this.on('resize.Helpers:autoUpdate', () => this.update())
            this._autoUpdate = true
        } else if (!enable && this._autoUpdate) {
            this.off('resize.Helpers:autoUpdate')
            this._autoUpdate = false
        }
    },

    // Update custom option based on element
    updateCustomOptionCheck(el) {
        if (el.checked) {
            // If custom option element is radio, remove checked from the siblings (closest `.row`)
            if (el.type === 'radio') {
                const customRadioOptionList = [].slice.call(el.closest('.row').querySelectorAll('.custom-option'))
                customRadioOptionList.map(function (customRadioOptionEL) {
                    customRadioOptionEL.closest('.custom-option').classList.remove('checked')
                })
            }
            el.closest('.custom-option').classList.add('checked')
        } else {
            el.closest('.custom-option').classList.remove('checked')
        }
    },

    // *******************************************************************************
    // * Tests

    isRtl() {
        return (
            document.querySelector('body').getAttribute('dir') === 'rtl' ||
            document.querySelector('html').getAttribute('dir') === 'rtl'
        )
    },

    isMobileDevice() {
        return typeof window.orientation !== 'undefined' || navigator.userAgent.indexOf('IEMobile') !== -1
    },

    isSmallScreen() {
        return (
            (window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth) < this.LAYOUT_BREAKPOINT
        )
    },

    isLayoutNavbarFull() {
        return !!document.querySelector('.layout-wrapper.layout-navbar-full')
    },

    isCollapsed() {
        if (this.isSmallScreen()) {
            return !this._hasClass('layout-menu-expanded')
        }
        return this._hasClass('layout-menu-collapsed')
    },

    isFixed() {
        return this._hasClass('layout-menu-fixed layout-menu-fixed-offcanvas')
    },

    isOffcanvas() {
        return this._hasClass('layout-menu-offcanvas layout-menu-fixed-offcanvas')
    },

    isNavbarFixed() {
        return (
            this._hasClass('layout-navbar-fixed') || (!this.isSmallScreen() && this.isFixed() && this.isLayoutNavbarFull())
        )
    },

    isFooterFixed() {
        return this._hasClass('layout-footer-fixed')
    },

    isFlipped() {
        return this._hasClass('layout-menu-flipped')
    },

    isLightStyle() {
        return document.documentElement.classList.contains('light-style')
    },

    isDarkStyle() {
        return document.documentElement.classList.contains('dark-style')
    },

    // *******************************************************************************
    // * Events

    on(event = requiredParam('event'), callback = requiredParam('callback')) {
        const [_event] = event.split('.')
        let [, ...namespace] = event.split('.')
        // let [_event, ...namespace] = event.split('.')
        namespace = namespace.join('.') || null

        this._listeners.push({ event: _event, namespace, callback })
    },

    off(event = requiredParam('event')) {
        const [_event] = event.split('.')
        let [, ...namespace] = event.split('.')
        namespace = namespace.join('.') || null

        this._listeners
            .filter(listener => listener.event === _event && listener.namespace === namespace)
            .forEach(listener => this._listeners.splice(this._listeners.indexOf(listener), 1))
    },

    // *******************************************************************************
    // * Life cycle

    init() {
        if (this._initialized) return
        this._initialized = true

        // Initialize `style` element
        this._updateInlineStyle(0)

        // Bind window resize event
        this._bindWindowResizeEvent()

        // Bind init event
        this.off('init._Helpers')
        this.on('init._Helpers', () => {
            this.off('resize._Helpers:redrawMenu')
            this.on('resize._Helpers:redrawMenu', () => {
                // eslint-disable-next-line no-unused-expressions
                this.isSmallScreen() && !this.isCollapsed() && this._redrawLayoutMenu()
            })

            // Force repaint in IE 10
            if (typeof document.documentMode === 'number' && document.documentMode < 11) {
                this.off('resize._Helpers:ie10RepaintBody')
                this.on('resize._Helpers:ie10RepaintBody', () => {
                    if (this.isFixed()) return
                    const { scrollTop } = document.documentElement
                    document.body.style.display = 'none'
                    // document.body.offsetHeight
                    document.body.style.display = 'block'
                    document.documentElement.scrollTop = scrollTop
                })
            }
        })

        this._triggerEvent('init')
    },

    destroy() {
        if (!this._initialized) return
        this._initialized = false

        this._removeClass('layout-transitioning')
        this._removeInlineStyle()
        this._unbindLayoutAnimationEndEvent()
        this._unbindWindowResizeEvent()
        this._unbindMenuMouseEvents()
        this.setAutoUpdate(false)

        this.off('init._Helpers')

        // Remove all listeners except `init`
        this._listeners
            .filter(listener => listener.event !== 'init')
            .forEach(listener => this._listeners.splice(this._listeners.indexOf(listener), 1))
    },

    // ---
    // Init Password Toggle
    initPasswordToggle() {
        const toggler = document.querySelectorAll('.form-password-toggle i')
        if (typeof toggler !== 'undefined' && toggler !== null) {
            toggler.forEach(el => {
                el.addEventListener('click', e => {
                    e.preventDefault()
                    const formPasswordToggle = el.closest('.form-password-toggle')
                    const formPasswordToggleIcon = formPasswordToggle.querySelector('i')
                    const formPasswordToggleInput = formPasswordToggle.querySelector('input')

                    if (formPasswordToggleInput.getAttribute('type') === 'text') {
                        formPasswordToggleInput.setAttribute('type', 'password')
                        formPasswordToggleIcon.classList.replace('bx-show', 'bx-hide')
                    } else if (formPasswordToggleInput.getAttribute('type') === 'password') {
                        formPasswordToggleInput.setAttribute('type', 'text')
                        formPasswordToggleIcon.classList.replace('bx-hide', 'bx-show')
                    }
                })
            })
        }
    },

    //--
    // Init custom option check
    initCustomOptionCheck() {
        const _this = this

        const custopOptionList = [].slice.call(document.querySelectorAll('.custom-option .form-check-input'))
        custopOptionList.map(function (customOptionEL) {
            // Update custom options check on page load
            _this.updateCustomOptionCheck(customOptionEL)

            // Update custom options check on click
            customOptionEL.addEventListener('click', e => {
                _this.updateCustomOptionCheck(customOptionEL)
            })
        })
    },

    // ---
    // Init Speech To Text
    initSpeechToText() {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
        const speechToText = document.querySelectorAll('.speech-to-text')
        if (SpeechRecognition !== undefined && SpeechRecognition !== null) {
            if (typeof speechToText !== 'undefined' && speechToText !== null) {
                const recognition = new SpeechRecognition()
                const toggler = document.querySelectorAll('.speech-to-text i')
                toggler.forEach(el => {
                    let listening = false
                    el.addEventListener('click', () => {
                        el.closest('.input-group').querySelector('.form-control').focus()
                        recognition.onspeechstart = () => {
                            listening = true
                        }
                        if (listening === false) {
                            recognition.start()
                        }
                        recognition.onerror = () => {
                            listening = false
                        }
                        recognition.onresult = event => {
                            el.closest('.input-group').querySelector('.form-control').value = event.results[0][0].transcript
                        }
                        recognition.onspeechend = () => {
                            listening = false
                            recognition.stop()
                        }
                    })
                })
            }
        }
    },

    // ---
    // Init Navbar Dropdown (i.e notification) PerfectScrollbar
    initNavbarDropdownScrollbar() {
        const scrollbarContainer = document.querySelectorAll('.navbar-dropdown .scrollable-container')
        const { PerfectScrollbar } = window

        if (PerfectScrollbar !== undefined) {
            if (typeof scrollbarContainer !== 'undefined' && scrollbarContainer !== null) {
                scrollbarContainer.forEach(el => {
                    // eslint-disable-next-line no-new
                    new PerfectScrollbar(el, {
                        wheelPropagation: false,
                        suppressScrollX: true
                    })
                })
            }
        }
    },

    // Ajax Call Promise
    ajaxCall(url) {
        return new Promise((resolve, reject) => {
            const req = new XMLHttpRequest()
            req.open('GET', url)
            req.onload = () => (req.status === 200 ? resolve(req.response) : reject(Error(req.statusText)))
            req.onerror = e => reject(Error(`Network Error: ${e}`))
            req.send()
        })
    },

    // ---
    // SidebarToggle (Used in Apps)
    initSidebarToggle() {
        const sidebarToggler = document.querySelectorAll('[data-bs-toggle="sidebar"]')

        sidebarToggler.forEach(el => {
            el.addEventListener('click', () => {
                const target = el.getAttribute('data-target')
                const overlay = el.getAttribute('data-overlay')
                const appOverlay = document.querySelectorAll('.app-overlay')
                const targetEl = document.querySelectorAll(target)

                targetEl.forEach(tel => {
                    tel.classList.toggle('show')
                    if (
                        typeof overlay !== 'undefined' &&
                        overlay !== null &&
                        overlay !== false &&
                        typeof appOverlay !== 'undefined'
                    ) {
                        if (tel.classList.contains('show')) {
                            appOverlay[0].classList.add('show')
                        } else {
                            appOverlay[0].classList.remove('show')
                        }
                        appOverlay[0].addEventListener('click', e => {
                            e.currentTarget.classList.remove('show')
                            tel.classList.remove('show')
                        })
                    }
                })
            })
        })
    }
}

class Menu {
    constructor(el, config = {}, _PS = null) {
        this._el = el
        this._horizontal = config.orientation === 'horizontal'
        this._animate = config.animate !== false
        this._accordion = config.accordion !== false
        this._showDropdownOnHover = Boolean(config.showDropdownOnHover)
        this._closeChildren = Boolean(config.closeChildren)
        this._rtl = document.documentElement.getAttribute('dir') === 'rtl' || document.body.getAttribute('dir') === 'rtl'

        this._onOpen = config.onOpen || (() => { })
        this._onOpened = config.onOpened || (() => { })
        this._onClose = config.onClose || (() => { })
        this._onClosed = config.onClosed || (() => { })

        this._psScroll = null
        this._topParent = null
        this._menuBgClass = null

        el.classList.add('menu')
        el.classList[this._animate ? 'remove' : 'add']('menu-no-animation')

        if (!this._horizontal) {
            el.classList.add('menu-vertical')
            el.classList.remove('menu-horizontal')

            const PerfectScrollbarLib = _PS || window.PerfectScrollbar

            if (PerfectScrollbarLib) {
                this._scrollbar = new PerfectScrollbarLib(el.querySelector('.menu-inner'), {
                    suppressScrollX: true,
                    wheelPropagation: !Menu._hasClass('layout-menu-fixed layout-menu-fixed-offcanvas')
                })

                Helpers.menuPsScroll = this._scrollbar
            } else {
                el.querySelector('.menu-inner').classList.add('overflow-auto')
            }
        } else {
            el.classList.add('menu-horizontal')
            el.classList.remove('menu-vertical')

            this._inner = el.querySelector('.menu-inner')
            const container = this._inner.parentNode

            this._prevBtn = el.querySelector('.menu-horizontal-prev')
            if (!this._prevBtn) {
                this._prevBtn = document.createElement('a')
                this._prevBtn.href = '#'
                this._prevBtn.className = 'menu-horizontal-prev'
                container.appendChild(this._prevBtn)
            }

            this._wrapper = el.querySelector('.menu-horizontal-wrapper')
            if (!this._wrapper) {
                this._wrapper = document.createElement('div')
                this._wrapper.className = 'menu-horizontal-wrapper'
                this._wrapper.appendChild(this._inner)
                container.appendChild(this._wrapper)
            }

            this._nextBtn = el.querySelector('.menu-horizontal-next')
            if (!this._nextBtn) {
                this._nextBtn = document.createElement('a')
                this._nextBtn.href = '#'
                this._nextBtn.className = 'menu-horizontal-next'
                container.appendChild(this._nextBtn)
            }

            this._innerPosition = 0
            this.update()
        }

        // Add data attribute for bg color class of menu
        const menuClassList = el.classList

        for (let i = 0; i < menuClassList.length; i++) {
            if (menuClassList[i].startsWith('bg-')) {
                this._menuBgClass = menuClassList[i]
            }
        }
        el.setAttribute('data-bg-class', this._menuBgClass)

        // Switch to vertical menu on small screen for horizontal menu layout on page load
        if (this._horizontal && window.innerWidth < Helpers.LAYOUT_BREAKPOINT) this.switchMenu('vertical')

        this._bindEvents()

        // Link menu instance to element
        el.menuInstance = this
    }

    _bindEvents() {
        // Click Event
        this._evntElClick = e => {
            // Find top parent element
            if (e.target.closest('ul') && e.target.closest('ul').classList.contains('menu-inner')) {
                const menuItem = Menu._findParent(e.target, 'menu-item', false)

                // eslint-disable-next-line prefer-destructuring
                if (menuItem) this._topParent = menuItem.childNodes[0]
            }

            const toggleLink = e.target.classList.contains('menu-toggle')
                ? e.target
                : Menu._findParent(e.target, 'menu-toggle', false)

            if (toggleLink) {
                e.preventDefault()

                if (toggleLink.getAttribute('data-hover') !== 'true') {
                    this.toggle(toggleLink)
                }
            }
        }
        if ((!this._showDropdownOnHover && this._horizontal) || !this._horizontal || Helpers.isMobileDevice)
            this._el.addEventListener('click', this._evntElClick)

        this._evntWindowResize = () => {
            this.update()
            if (this._lastWidth !== window.innerWidth) {
                this._lastWidth = window.innerWidth
                this.update()
            }

            const horizontalMenuTemplate = document.querySelector("[data-template^='horizontal-menu']")
            if (!this._horizontal && !horizontalMenuTemplate) this.manageScroll()
        }
        window.addEventListener('resize', this._evntWindowResize)

        if (this._horizontal) {
            this._evntPrevBtnClick = e => {
                e.preventDefault()
                if (this._prevBtn.classList.contains('disabled')) return
                this._slide('prev')
            }
            this._prevBtn.addEventListener('click', this._evntPrevBtnClick)

            this._evntNextBtnClick = e => {
                e.preventDefault()
                if (this._nextBtn.classList.contains('disabled')) return
                this._slide('next')
            }
            this._nextBtn.addEventListener('click', this._evntNextBtnClick)

            this._evntBodyClick = e => {
                if (!this._inner.contains(e.target) && this._el.querySelectorAll('.menu-inner > .menu-item.open').length)
                    this.closeAll()
            }
            document.body.addEventListener('click', this._evntBodyClick)

            if (this._showDropdownOnHover) {
                /** ***********************************************
                 * Horizontal Menu Mouse Over Event
                 * ? e.target !== e.currentTarget condition to disable mouseover event on whole menu navbar
                 * ? !e.target.parentNode.classList.contains('open') to disable mouseover events on icon, text and dropdown arrow
                 */
                this._evntElMouseOver = e => {
                    if (e.target !== e.currentTarget && !e.target.parentNode.classList.contains('open')) {
                        const toggleLink = e.target.classList.contains('menu-toggle') ? e.target : null

                        if (toggleLink) {
                            e.preventDefault()

                            if (toggleLink.getAttribute('data-hover') !== 'true') {
                                this.toggle(toggleLink)
                            }
                        }
                    }
                    e.stopPropagation()
                }
                if (this._horizontal && window.screen.width > Helpers.LAYOUT_BREAKPOINT) {
                    this._el.addEventListener('mouseover', this._evntElMouseOver)
                }

                /** ***********************************************
                 * Horizontal Menu Mouse Out Event
                 * ? e.target !== e.currentTarget condition to disable mouseout event on whole menu navbar
                 * ? mouseOutEl.parentNode.classList.contains('open') to check if the mouseout element has open class or not
                 * ? !mouseOutEl.classList.contains('menu-toggle') to check if mouseout was from single menu item and not from the one which has submenu
                 * ? !mouseOverEl.parentNode.classList.contains('menu-link') to disable mouseout event for icon, text and dropdown arrow
                 */
                this._evntElMouseOut = e => {
                    const mainEl = e.currentTarget
                    const mouseOutEl = e.target
                    const mouseOverEl = e.toElement || e.relatedTarget

                    // Find absolute parent of any menu item from which mouseout event triggered
                    if (mouseOutEl.closest('ul') && mouseOutEl.closest('ul').classList.contains('menu-inner')) {
                        this._topParent = mouseOutEl
                    }

                    if (
                        mouseOutEl !== mainEl &&
                        (mouseOutEl.parentNode.classList.contains('open') || !mouseOutEl.classList.contains('menu-toggle')) &&
                        mouseOverEl &&
                        mouseOverEl.parentNode &&
                        !mouseOverEl.parentNode.classList.contains('menu-link')
                    ) {
                        // When mouse goes totally out of menu items, check mouse over element to confirm it's not the child of menu, once confirmed close the menu
                        if (this._topParent && !Menu.childOf(mouseOverEl, this._topParent.parentNode)) {
                            const toggleLink = this._topParent.classList.contains('menu-toggle') ? this._topParent : null

                            if (toggleLink) {
                                e.preventDefault()

                                if (toggleLink.getAttribute('data-hover') !== 'true') {
                                    this.toggle(toggleLink)
                                    this._topParent = null
                                }
                            }
                        }

                        // When mouse enter the sub menu, check if it's child of the initially mouse overed menu item(Actual Parent),
                        // if it's the parent do not close the sub menu else close the sub menu
                        if (Menu.childOf(mouseOverEl, mouseOutEl.parentNode)) {
                            return
                        }
                        const toggleLink = mouseOutEl.classList.contains('menu-toggle') ? mouseOutEl : null

                        if (toggleLink) {
                            e.preventDefault()

                            if (toggleLink.getAttribute('data-hover') !== 'true') {
                                this.toggle(toggleLink)
                            }
                        }
                    }
                    e.stopPropagation()
                }
                if (this._horizontal && window.screen.width > Helpers.LAYOUT_BREAKPOINT) {
                    this._el.addEventListener('mouseout', this._evntElMouseOut)
                }
            }
        }
    }

    static childOf(/* child node */ c, /* parent node */ p) {
        // returns boolean
        if (c.parentNode) {
            while ((c = c.parentNode) && c !== p);
            return !!c
        }
        return false
    }

    _unbindEvents() {
        if (this._evntElClick) {
            this._el.removeEventListener('click', this._evntElClick)
            this._evntElClick = null
        }

        if (this._evntElMouseOver) {
            this._el.removeEventListener('mouseover', this._evntElMouseOver)
            this._evntElMouseOver = null
        }

        if (this._evntElMouseOut) {
            this._el.removeEventListener('mouseout', this._evntElMouseOut)
            this._evntElMouseOut = null
        }

        if (this._evntWindowResize) {
            window.removeEventListener('resize', this._evntWindowResize)
            this._evntWindowResize = null
        }

        if (this._evntBodyClick) {
            document.body.removeEventListener('click', this._evntBodyClick)
            this._evntBodyClick = null
        }

        if (this._evntInnerMousemove) {
            this._inner.removeEventListener('mousemove', this._evntInnerMousemove)
            this._evntInnerMousemove = null
        }

        if (this._evntInnerMouseleave) {
            this._inner.removeEventListener('mouseleave', this._evntInnerMouseleave)
            this._evntInnerMouseleave = null
        }
    }

    static _isRoot(item) {
        return !Menu._findParent(item, 'menu-item', false)
    }

    static _findParent(el, cls, throwError = true) {
        if (el.tagName.toUpperCase() === 'BODY') return null
        el = el.parentNode
        while (el.tagName.toUpperCase() !== 'BODY' && !el.classList.contains(cls)) {
            el = el.parentNode
        }

        el = el.tagName.toUpperCase() !== 'BODY' ? el : null

        if (!el && throwError) throw new Error(`Cannot find \`.${cls}\` parent element`)

        return el
    }

    static _findChild(el, cls) {
        const items = el.childNodes
        const found = []

        for (let i = 0, l = items.length; i < l; i++) {
            if (items[i].classList) {
                let passed = 0

                for (let j = 0; j < cls.length; j++) {
                    if (items[i].classList.contains(cls[j])) passed += 1
                }

                if (cls.length === passed) found.push(items[i])
            }
        }

        return found
    }

    static _findMenu(item) {
        let curEl = item.childNodes[0]
        let menu = null

        while (curEl && !menu) {
            if (curEl.classList && curEl.classList.contains('menu-sub')) menu = curEl
            curEl = curEl.nextSibling
        }

        if (!menu) throw new Error('Cannot find `.menu-sub` element for the current `.menu-toggle`')

        return menu
    }

    // Has class
    static _hasClass(cls, el = Helpers.ROOT_EL) {
        let result = false

        cls.split(' ').forEach(c => {
            if (el.classList.contains(c)) result = true
        })

        return result
    }

    open(el, closeChildren = this._closeChildren) {
        const item = this._findUnopenedParent(Menu._getItem(el, true), closeChildren)

        if (!item) return

        const toggleLink = Menu._getLink(item, true)

        Menu._promisify(this._onOpen, this, item, toggleLink, Menu._findMenu(item))
            .then(() => {
                if (!this._horizontal || !Menu._isRoot(item)) {
                    if (this._animate && !this._horizontal) {
                        window.requestAnimationFrame(() => this._toggleAnimation(true, item, false))
                        if (this._accordion) this._closeOther(item, closeChildren)
                    } else if (this._animate) {
                        this._toggleDropdown(true, item, closeChildren)
                        // eslint-disable-next-line no-unused-expressions
                        this._onOpened && this._onOpened(this, item, toggleLink, Menu._findMenu(item))
                    } else {
                        item.classList.add('open')
                        // eslint-disable-next-line no-unused-expressions
                        this._onOpened && this._onOpened(this, item, toggleLink, Menu._findMenu(item))
                        if (this._accordion) this._closeOther(item, closeChildren)
                    }
                } else {
                    this._toggleDropdown(true, item, closeChildren)
                    // eslint-disable-next-line no-unused-expressions
                    this._onOpened && this._onOpened(this, item, toggleLink, Menu._findMenu(item))
                }
            })
            .catch(() => { })
    }

    close(el, closeChildren = this._closeChildren, _autoClose = false) {
        const item = Menu._getItem(el, true)
        const toggleLink = Menu._getLink(el, true)

        if (!item.classList.contains('open') || item.classList.contains('disabled')) return

        Menu._promisify(this._onClose, this, item, toggleLink, Menu._findMenu(item), _autoClose)
            .then(() => {
                if (!this._horizontal || !Menu._isRoot(item)) {
                    if (this._animate && !this._horizontal) {
                        window.requestAnimationFrame(() => this._toggleAnimation(false, item, closeChildren))
                    } else {
                        item.classList.remove('open')

                        if (closeChildren) {
                            const opened = item.querySelectorAll('.menu-item.open')
                            for (let i = 0, l = opened.length; i < l; i++) opened[i].classList.remove('open')
                        }

                        // eslint-disable-next-line no-unused-expressions
                        this._onClosed && this._onClosed(this, item, toggleLink, Menu._findMenu(item))
                    }
                } else {
                    this._toggleDropdown(false, item, closeChildren)
                    // eslint-disable-next-line no-unused-expressions
                    this._onClosed && this._onClosed(this, item, toggleLink, Menu._findMenu(item))
                }
            })
            .catch(() => { })
    }

    _closeOther(item, closeChildren) {
        const opened = Menu._findChild(item.parentNode, ['menu-item', 'open'])

        for (let i = 0, l = opened.length; i < l; i++) {
            if (opened[i] !== item) this.close(opened[i], closeChildren)
        }
    }

    toggle(el, closeChildren = this._closeChildren) {
        const item = Menu._getItem(el, true)
        // const toggleLink = Menu._getLink(el, true)

        if (item.classList.contains('open')) this.close(item, closeChildren)
        else this.open(item, closeChildren)
    }

    _toggleDropdown(show, item, closeChildren) {
        const menu = Menu._findMenu(item)
        const actualItem = item
        let subMenuItem = false

        if (show) {
            if (Menu._findParent(item, 'menu-sub', false)) {
                subMenuItem = true
                item = this._topParent ? this._topParent.parentNode : item
            }

            const wrapperWidth = Math.round(this._wrapper.getBoundingClientRect().width)
            const position = this._innerPosition
            const itemOffset = this._getItemOffset(item)
            const itemWidth = Math.round(item.getBoundingClientRect().width)

            if (itemOffset - DELTA <= -1 * position) {
                this._innerPosition = -1 * itemOffset
            } else if (itemOffset + position + itemWidth + DELTA >= wrapperWidth) {
                if (itemWidth > wrapperWidth) {
                    this._innerPosition = -1 * itemOffset
                } else {
                    this._innerPosition = -1 * (itemOffset + itemWidth - wrapperWidth)
                }
            }

            actualItem.classList.add('open')

            const menuWidth = Math.round(menu.getBoundingClientRect().width)

            if (subMenuItem) {
                if (
                    itemOffset + this._innerPosition + menuWidth * 2 > wrapperWidth &&
                    menuWidth < wrapperWidth &&
                    menuWidth >= itemWidth
                ) {
                    menu.style.left = [this._rtl ? '100%' : '-100%']
                }
            } else if (
                itemOffset + this._innerPosition + menuWidth > wrapperWidth &&
                menuWidth < wrapperWidth &&
                menuWidth > itemWidth
            ) {
                menu.style[this._rtl ? 'marginRight' : 'marginLeft'] = `-${menuWidth - itemWidth}px`
            }

            this._closeOther(actualItem, closeChildren)
            this._updateSlider()
        } else {
            const toggle = Menu._findChild(item, ['menu-toggle'])

            // eslint-disable-next-line no-unused-expressions
            toggle.length && toggle[0].removeAttribute('data-hover', 'true')
            item.classList.remove('open')
            menu.style[this._rtl ? 'marginRight' : 'marginLeft'] = null

            if (closeChildren) {
                const opened = menu.querySelectorAll('.menu-item.open')

                for (let i = 0, l = opened.length; i < l; i++) opened[i].classList.remove('open')
            }
        }
    }

    _slide(direction) {
        const wrapperWidth = Math.round(this._wrapper.getBoundingClientRect().width)
        const innerWidth = this._innerWidth
        let newPosition

        if (direction === 'next') {
            newPosition = this._getSlideNextPos()

            if (innerWidth + newPosition < wrapperWidth) {
                newPosition = wrapperWidth - innerWidth
            }
        } else {
            newPosition = this._getSlidePrevPos()

            if (newPosition > 0) newPosition = 0
        }

        this._innerPosition = newPosition
        this.update()
    }

    _getSlideNextPos() {
        const wrapperWidth = Math.round(this._wrapper.getBoundingClientRect().width)
        const position = this._innerPosition
        let curItem = this._inner.childNodes[0]
        let left = 0

        while (curItem) {
            if (curItem.tagName) {
                const curItemWidth = Math.round(curItem.getBoundingClientRect().width)

                if (left + position - DELTA <= wrapperWidth && left + position + curItemWidth + DELTA >= wrapperWidth) {
                    if (curItemWidth > wrapperWidth && left === -1 * position) left += curItemWidth
                    break
                }

                left += curItemWidth
            }

            curItem = curItem.nextSibling
        }

        return -1 * left
    }

    _getSlidePrevPos() {
        const wrapperWidth = Math.round(this._wrapper.getBoundingClientRect().width)
        const position = this._innerPosition
        let curItem = this._inner.childNodes[0]
        let left = 0

        while (curItem) {
            if (curItem.tagName) {
                const curItemWidth = Math.round(curItem.getBoundingClientRect().width)

                if (left - DELTA <= -1 * position && left + curItemWidth + DELTA >= -1 * position) {
                    if (curItemWidth <= wrapperWidth) left = left + curItemWidth - wrapperWidth
                    break
                }

                left += curItemWidth
            }

            curItem = curItem.nextSibling
        }

        return -1 * left
    }

    static _getItem(el, toggle) {
        let item = null
        const selector = toggle ? 'menu-toggle' : 'menu-link'

        if (el.classList.contains('menu-item')) {
            if (Menu._findChild(el, [selector]).length) item = el
        } else if (el.classList.contains(selector)) {
            item = el.parentNode.classList.contains('menu-item') ? el.parentNode : null
        }

        if (!item) {
            throw new Error(`${toggle ? 'Toggable ' : ''}\`.menu-item\` element not found.`)
        }

        return item
    }

    static _getLink(el, toggle) {
        let found = []
        const selector = toggle ? 'menu-toggle' : 'menu-link'

        if (el.classList.contains(selector)) found = [el]
        else if (el.classList.contains('menu-item')) found = Menu._findChild(el, [selector])

        if (!found.length) throw new Error(`\`${selector}\` element not found.`)

        return found[0]
    }

    _findUnopenedParent(item, closeChildren) {
        let tree = []
        let parentItem = null

        while (item) {
            if (item.classList.contains('disabled')) {
                parentItem = null
                tree = []
            } else {
                if (!item.classList.contains('open')) parentItem = item
                tree.push(item)
            }

            item = Menu._findParent(item, 'menu-item', false)
        }

        if (!parentItem) return null
        if (tree.length === 1) return parentItem

        tree = tree.slice(0, tree.indexOf(parentItem))

        for (let i = 0, l = tree.length; i < l; i++) {
            tree[i].classList.add('open')

            if (this._accordion) {
                const openedItems = Menu._findChild(tree[i].parentNode, ['menu-item', 'open'])

                for (let j = 0, k = openedItems.length; j < k; j++) {
                    if (openedItems[j] !== tree[i]) {
                        openedItems[j].classList.remove('open')

                        if (closeChildren) {
                            const openedChildren = openedItems[j].querySelectorAll('.menu-item.open')
                            for (let x = 0, z = openedChildren.length; x < z; x++) {
                                openedChildren[x].classList.remove('open')
                            }
                        }
                    }
                }
            }
        }

        return parentItem
    }

    _toggleAnimation(open, item, closeChildren) {
        const toggleLink = Menu._getLink(item, true)
        const menu = Menu._findMenu(item)

        Menu._unbindAnimationEndEvent(item)

        const linkHeight = Math.round(toggleLink.getBoundingClientRect().height)

        item.style.overflow = 'hidden'

        const clearItemStyle = () => {
            item.classList.remove('menu-item-animating')
            item.classList.remove('menu-item-closing')
            item.style.overflow = null
            item.style.height = null

            if (!this._horizontal) this.update()
        }

        if (open) {
            item.style.height = `${linkHeight}px`
            item.classList.add('menu-item-animating')
            item.classList.add('open')

            Menu._bindAnimationEndEvent(item, () => {
                clearItemStyle()
                this._onOpened(this, item, toggleLink, menu)
            })

            setTimeout(() => {
                item.style.height = `${linkHeight + Math.round(menu.getBoundingClientRect().height)}px`
            }, 50)
        } else {
            item.style.height = `${linkHeight + Math.round(menu.getBoundingClientRect().height)}px`
            item.classList.add('menu-item-animating')
            item.classList.add('menu-item-closing')

            Menu._bindAnimationEndEvent(item, () => {
                item.classList.remove('open')
                clearItemStyle()

                if (closeChildren) {
                    const opened = item.querySelectorAll('.menu-item.open')
                    for (let i = 0, l = opened.length; i < l; i++) opened[i].classList.remove('open')
                }

                this._onClosed(this, item, toggleLink, menu)
            })

            setTimeout(() => {
                item.style.height = `${linkHeight}px`
            }, 50)
        }
    }

    static _bindAnimationEndEvent(el, handler) {
        const cb = e => {
            if (e.target !== el) return
            Menu._unbindAnimationEndEvent(el)
            handler(e)
        }

        let duration = window.getComputedStyle(el).transitionDuration
        duration = parseFloat(duration) * (duration.indexOf('ms') !== -1 ? 1 : 1000)

        el._menuAnimationEndEventCb = cb
        TRANSITION_EVENTS.forEach(ev => el.addEventListener(ev, el._menuAnimationEndEventCb, false))

        el._menuAnimationEndEventTimeout = setTimeout(() => {
            cb({ target: el })
        }, duration + 50)
    }

    _getItemOffset(item) {
        let curItem = this._inner.childNodes[0]
        let left = 0

        while (curItem !== item) {
            if (curItem.tagName) {
                left += Math.round(curItem.getBoundingClientRect().width)
            }

            curItem = curItem.nextSibling
        }

        return left
    }

    _updateSlider(wrapperWidth = null, innerWidth = null, position = null) {
        const _wrapperWidth = wrapperWidth !== null ? wrapperWidth : Math.round(this._wrapper.getBoundingClientRect().width)
        const _innerWidth = innerWidth !== null ? innerWidth : this._innerWidth
        const _position = position !== null ? position : this._innerPosition

        if (_innerWidth < _wrapperWidth || window.innerWidth < Helpers.LAYOUT_BREAKPOINT) {
            this._prevBtn.classList.add('d-none')
            this._nextBtn.classList.add('d-none')
        } else {
            this._prevBtn.classList.remove('d-none')
            this._nextBtn.classList.remove('d-none')
        }
        if (_innerWidth > _wrapperWidth && window.innerWidth > Helpers.LAYOUT_BREAKPOINT) {
            if (_position === 0) this._prevBtn.classList.add('disabled')
            else this._prevBtn.classList.remove('disabled')

            if (_innerWidth + _position <= _wrapperWidth) this._nextBtn.classList.add('disabled')
            else this._nextBtn.classList.remove('disabled')
        }
    }

    static _promisify(fn, ...args) {
        const result = fn(...args)
        if (result instanceof Promise) {
            return result
        }
        if (result === false) {
            return Promise.reject()
        }
        return Promise.resolve()
    }

    get _innerWidth() {
        const items = this._inner.childNodes
        let width = 0

        for (let i = 0, l = items.length; i < l; i++) {
            if (items[i].tagName) {
                width += Math.round(items[i].getBoundingClientRect().width)
            }
        }

        return width
    }

    get _innerPosition() {
        return parseInt(this._inner.style[this._rtl ? 'marginRight' : 'marginLeft'] || '0px', 10)
    }

    set _innerPosition(value) {
        this._inner.style[this._rtl ? 'marginRight' : 'marginLeft'] = `${value}px`
        return value
    }

    static _unbindAnimationEndEvent(el) {
        const cb = el._menuAnimationEndEventCb

        if (el._menuAnimationEndEventTimeout) {
            clearTimeout(el._menuAnimationEndEventTimeout)
            el._menuAnimationEndEventTimeout = null
        }

        if (!cb) return

        TRANSITION_EVENTS.forEach(ev => el.removeEventListener(ev, cb, false))
        el._menuAnimationEndEventCb = null
    }

    closeAll(closeChildren = this._closeChildren) {
        const opened = this._el.querySelectorAll('.menu-inner > .menu-item.open')

        for (let i = 0, l = opened.length; i < l; i++) this.close(opened[i], closeChildren)
    }

    static setDisabled(el, disabled) {
        Menu._getItem(el, false).classList[disabled ? 'add' : 'remove']('disabled')
    }

    static isActive(el) {
        return Menu._getItem(el, false).classList.contains('active')
    }

    static isOpened(el) {
        return Menu._getItem(el, false).classList.contains('open')
    }

    static isDisabled(el) {
        return Menu._getItem(el, false).classList.contains('disabled')
    }

    update() {
        if (!this._horizontal) {
            if (this._scrollbar) {
                this._scrollbar.update()
            }
        } else {
            this.closeAll()

            const wrapperWidth = Math.round(this._wrapper.getBoundingClientRect().width)
            const innerWidth = this._innerWidth
            let position = this._innerPosition

            if (wrapperWidth - position > innerWidth) {
                position = wrapperWidth - innerWidth
                if (position > 0) position = 0
                this._innerPosition = position
            }

            this._updateSlider(wrapperWidth, innerWidth, position)
        }
    }

    manageScroll() {
        const { PerfectScrollbar } = window
        const menuInner = document.querySelector('.menu-inner')

        if (window.innerWidth < Helpers.LAYOUT_BREAKPOINT) {
            if (this._scrollbar !== null) {
                // Helpers.menuPsScroll.destroy()
                // this._scrollbar.destroy()
                this._scrollbar = null
            }
            menuInner.classList.add('overflow-auto')
        } else {
            if (this._scrollbar === null) {
                const menuScroll = new PerfectScrollbar(document.querySelector('.menu-inner'), {
                    suppressScrollX: true,
                    wheelPropagation: false
                })
                // window.Helpers.menuPsScroll = menuScroll
                this._scrollbar = menuScroll
            }
            menuInner.classList.remove('overflow-auto')
        }
    }

    switchMenu(menu) {
        // Unbind Events
        this._unbindEvents()

        // const html = document.documentElement
        const navbar = document.querySelector('nav.layout-navbar')
        const navbarCollapse = document.querySelector('#navbar-collapse')
        /* const fullNavbar = document.querySelector('.layout-navbar-full')
        const contentNavbar = document.querySelector('.layout-content-navbar')
        const contentWrapper = document.querySelector('.content-wrapper') */
        const asideMenuWrapper = document.querySelector('#layout-menu div')
        const asideMenu = document.querySelector('#layout-menu')
        const horzMenuClasses = ['layout-menu-horizontal', 'menu', 'menu-horizontal', 'container-fluid', 'flex-grow-0']
        const vertMenuClasses = ['layout-menu', 'menu', 'menu-vertical']
        const horzMenuWrapper = document.querySelector('.menu-horizontal-wrapper')
        const menuInner = document.querySelector('.menu-inner')
        const brand = document.querySelector('.app-brand')
        const menuToggler = document.querySelector('.layout-menu-toggle')
        const activeMenuItems = document.querySelectorAll('.menu-inner .active')
        /* const layoutPage = document.querySelector('.layout-page')
        const layoutContainer = document.querySelector('.layout-container')
        const content = document.querySelector('.container-fluid') */

        // const { PerfectScrollbar } = window

        if (menu === 'vertical') {
            this._horizontal = false
            asideMenuWrapper.insertBefore(brand, horzMenuWrapper)
            asideMenuWrapper.insertBefore(menuInner, horzMenuWrapper)
            asideMenuWrapper.classList.add('flex-column', 'p-0')
            asideMenu.classList.remove(...asideMenu.classList)
            asideMenu.classList.add(...vertMenuClasses, this._menuBgClass)
            brand.classList.remove('d-none', 'd-lg-flex')
            menuToggler.classList.remove('d-none')
            // if (PerfectScrollbar !== undefined) {
            //   this._psScroll = new PerfectScrollbar(document.querySelector('.menu-inner'), {
            //     suppressScrollX: true,
            //     wheelPropagation: !Menu._hasClass('layout-menu-fixed layout-menu-fixed-offcanvas')
            //   })
            // }

            menuInner.classList.add('overflow-auto')

            // Add open class to active items
            for (let i = 0; i < activeMenuItems.length - 1; ++i) {
                activeMenuItems[i].classList.add('open')
            }
        } else {
            this._horizontal = true
            navbar.children[0].insertBefore(brand, navbarCollapse)
            brand.classList.add('d-none', 'd-lg-flex')
            horzMenuWrapper.appendChild(menuInner)
            asideMenuWrapper.classList.remove('flex-column', 'p-0')
            asideMenu.classList.remove(...asideMenu.classList)
            asideMenu.classList.add(...horzMenuClasses, this._menuBgClass)
            menuToggler.classList.add('d-none')
            menuInner.classList.remove('overflow-auto')

            // if (PerfectScrollbar !== undefined && this._psScroll !== null) {
            //   this._psScroll.destroy()
            //   this._psScroll = null
            // }

            // Remove open class from active items
            for (let i = 0; i < activeMenuItems.length; ++i) {
                activeMenuItems[i].classList.remove('open')
            }
        }

        this._bindEvents()
    }

    destroy() {
        if (!this._el) return

        this._unbindEvents()

        const items = this._el.querySelectorAll('.menu-item')
        for (let i = 0, l = items.length; i < l; i++) {
            Menu._unbindAnimationEndEvent(items[i])
            items[i].classList.remove('menu-item-animating')
            items[i].classList.remove('open')
            items[i].style.overflow = null
            items[i].style.height = null
        }

        const menus = this._el.querySelectorAll('.menu-menu')
        for (let i2 = 0, l2 = menus.length; i2 < l2; i2++) {
            menus[i2].style.marginRight = null
            menus[i2].style.marginLeft = null
        }

        this._el.classList.remove('menu-no-animation')

        if (this._wrapper) {
            this._prevBtn.parentNode.removeChild(this._prevBtn)
            this._nextBtn.parentNode.removeChild(this._nextBtn)
            this._wrapper.parentNode.insertBefore(this._inner, this._wrapper)
            this._wrapper.parentNode.removeChild(this._wrapper)
            this._inner.style.marginLeft = null
            this._inner.style.marginRight = null
        }

        this._el.menuInstance = null
        delete this._el.menuInstance

        this._el = null
        this._horizontal = null
        this._animate = null
        this._accordion = null
        this._showDropdownOnHover = null
        this._closeChildren = null
        this._rtl = null
        this._onOpen = null
        this._onOpened = null
        this._onClose = null
        this._onClosed = null
        if (this._scrollbar) {
            this._scrollbar.destroy()
            this._scrollbar = null
        }
        this._inner = null
        this._prevBtn = null
        this._wrapper = null
        this._nextBtn = null
    }
}

onMounted(() => {
    let layoutMenuEl = document.querySelectorAll('#layout-menu');
    layoutMenuEl.forEach(function (element) {
        menu = new Menu(element, {
            orientation: isHorizontalLayout ? 'horizontal' : 'vertical',
            closeChildren: isHorizontalLayout ? true : false,
            // ? This option only works with Horizontal menu

        });
        // Change parameter to true if you want scroll animation
        Helpers.scrollToActive((animate = false));
        Helpers.mainMenu = menu;
    });

    // Initialize menu togglers and bind click on each
    let menuToggler = document.querySelectorAll('.layout-menu-toggle');
    menuToggler.forEach(item => {
        item.addEventListener('click', event => {
            event.preventDefault();
            Helpers.toggleCollapsed();
            // Enable menu state with local storage support if enableMenuLocalStorage = true from config.js

        });
    });

    // Display menu toggle (layout-menu-toggle) on hover with delay
    let delay = function (elem, callback) {
        let timeout = null;
        elem.onmouseenter = function () {
            // Set timeout to be a timer which will invoke callback after 300ms (not for small screen)
            if (!Helpers.isSmallScreen()) {
                timeout = setTimeout(callback, 300);
            } else {
                timeout = setTimeout(callback, 0);
            }
        };

        elem.onmouseleave = function () {
            // Clear any timers set to timeout
            document.querySelector('.layout-menu-toggle').classList.remove('d-block');
            clearTimeout(timeout);
        };
    };
    if (document.getElementById('layout-menu')) {
        delay(document.getElementById('layout-menu'), function () {
            // not for small screen
            if (!Helpers.isSmallScreen()) {
                document.querySelector('.layout-menu-toggle').classList.add('d-block');
            }
        });
    }

    // Menu swipe gesture

    // Detect swipe gesture on the target element and call swipe In
    Helpers.swipeIn('.drag-target', function (e) {
        Helpers.setCollapsed(false);
    });

    // Detect swipe gesture on the target element and call swipe Out
    Helpers.swipeOut('#layout-menu', function (e) {
        if (Helpers.isSmallScreen()) Helpers.setCollapsed(true);
    });

    // Display in main menu when menu scrolls
    let menuInnerContainer = document.getElementsByClassName('menu-inner'),
        menuInnerShadow = document.getElementsByClassName('menu-inner-shadow')[0];
    if (menuInnerContainer.length > 0 && menuInnerShadow) {
        menuInnerContainer[0].addEventListener('ps-scroll-y', function () {
            if (this.querySelector('.ps__thumb-y').offsetTop) {
                menuInnerShadow.style.display = 'block';
            } else {
                menuInnerShadow.style.display = 'none';
            }
        });
    }

    // Init PerfectScrollbar in Navbar Dropdown (i.e notification)
    Helpers.initNavbarDropdownScrollbar();

});

</script>

<template>
    <aside id="layout-menu" class="layout-menu menu-vertical menu bg-menu-theme">
        <div class="app-brand demo">
            <Link href="/" class="app-brand-link">
            <img src="/ui/img/branding/logo-white.png" alt="" height="36" />
            </Link>
            <!-- <a href="javascript:void(0);" class="layout-menu-toggle menu-link text-large ms-auto">
                    <i class="bx bx-chevron-left bx-sm align-middle"></i>
                </a> -->
        </div>
        <div class="menu-inner-shadow"></div>
        <ul class="menu-inner py-1">
            <PerfectScrollbar class="menu-inner" :options="{ wheelPropagation: false }">
                <!-- Dashboards -->
                <li class="menu-item" :class="{ 'active': $page.url.startsWith('/dashboard') }">
                    <Link href="/dashboard" class="menu-link" preserveScroll>
                    <i class="menu-icon tf-icons bx bxs-dashboard"></i>
                    <div>Dashboard</div>
                    </Link>
                </li>

                <!-- properties -->
                <li class="menu-header small text-uppercase"
                    v-if="$page.props.user.permissions.includes('hotel-manager')">
                    <span class="menu-header-text">Properties</span>
                </li>
                <li class="menu-item"
                    :class="{ 'active open': $page.component.startsWith('Hotels') || $page.component.startsWith('Rooms') || $page.component.startsWith('Mealtypes') || $page.component.startsWith('Ageranges') || $page.component.startsWith('Transfers') || $page.component.startsWith('Facilities') || $page.component.startsWith('RoomCategories') || $page.component.startsWith('Gallery') }"
                    v-if="$page.props.user.permissions.includes('hotel-manager')">
                    <a href="javascript:void(0);" class="menu-link menu-toggle">
                        <i class="menu-icon tf-icons bx bx-hotel"></i>
                        <div>Hotel Manager</div>
                    </a>
                    <ul class="menu-sub">
                        <li class="menu-item"
                            :class="{ 'active': $page.component.startsWith('Hotels') || $page.component.startsWith('Rooms') || $page.component.startsWith('Mealtypes') || $page.component.startsWith('Ageranges') || $page.component.startsWith('Transfers') || $page.component.startsWith('Facilities') || $page.component.startsWith('Gallery') }">
                            <Link href="/hotels" preserve-state preserveScroll class="menu-link">
                            <div>Manage Hotels</div>
                            </Link>
                        </li>
                        <li class="menu-item" :class="{ 'active': $page.component.startsWith('RoomCategories') }"
                            v-if="$page.props.user.permissions.includes('view-room-categories')">
                            <Link href="/roomcategories" preserve-state preserveScroll class="menu-link">
                            <div>Room Categories</div>
                            </Link>
                        </li>
                    </ul>
                </li>

                <!-- reservations -->
                <li class="menu-header small text-uppercase"
                    v-if="$page.props.user.permissions.includes('booking-manager')">
                    <span class="menu-header-text">Reservations</span>
                </li>
                <!-- Cards -->
                <li class="menu-item"
                    :class="{ 'active open': $page.component.startsWith('Bookings') || $page.component.startsWith('Clients') || $page.component.startsWith('Invoices') || $page.component.startsWith('OtherBookings') }"
                    v-if="$page.props.user.permissions.includes('booking-manager')">
                    <a href="javascript:void(0);" class="menu-link menu-toggle">
                        <i class="menu-icon tf-icons bx bx-book"></i>
                        <div>Booking Manager</div>
                    </a>
                    <ul class="menu-sub">
                        <li class="menu-item"
                            :class="{ 'active': $page.component.startsWith('Bookings') || $page.component.startsWith('Clients') || $page.component.startsWith('Invoices') }"
                            v-if="$page.props.user.permissions.includes('booking-manager')">
                            <Link href="/bookings" preserve-state preserveScroll class="menu-link">
                            <div>Manage Bookings</div>
                            </Link>
                        </li>
                        <li class="menu-item" :class="{ 'active': $page.component.startsWith('OtherBookings') }"
                            v-if="$page.props.user.permissions.includes('booking-manager')">
                            <Link href="/other-bookings" preserve-state preserveScroll class="menu-link">
                            <div>Other Destinations</div>
                            </Link>
                        </li>
                    </ul>
                </li>

                <li class="menu-header small text-uppercase"
                    v-if="$page.props.user.permissions.includes('view-package-search') || $page.props.user.permissions.includes('my-bookings')">
                    <span class="menu-header-text">Bookings</span>
                </li>
                <li class="menu-item" :class="{ 'active': $page.url.startsWith('/bookings/searchAccomodation') }"
                    v-if="$page.props.user.permissions.includes('view-package-search')">
                    <Link href="/bookings/searchAccomodation" class="menu-link" preserveScroll>
                    <i class="menu-icon tf-icons bx bx-search-alt"></i>
                    <div>Search Accomodation</div>
                    </Link>
                </li>
                <li class="menu-item" :class="{ 'active': $page.url.startsWith('/bookings/mybookings') }"
                    v-if="$page.props.user.permissions.includes('my-bookings')">
                    <Link href="/bookings/searchAccomodation" class="menu-link" preserveScroll>
                    <i class="menu-icon tf-icons bx bx-book"></i>
                    <div>My Bookings</div>
                    </Link>
                </li>

                <!-- packages -->
                <li class="menu-header small text-uppercase"
                    v-if="$page.props.user.permissions.includes('view-package-manager')">
                    <span class="menu-header-text">Packages</span>
                </li>
                <li class="menu-item" :class="{ 'active': $page.component.startsWith('Packages') }"
                    v-if="$page.props.user.permissions.includes('view-package-manager')">
                    <Link href="/packages" class="menu-link" preserveScroll>
                    <i class="menu-icon tf-icons bx bxs-package"></i>
                    <div>Package Manager</div>
                    </Link>
                </li>

                <!-- agents -->
                <li class="menu-header small text-uppercase"
                    v-if="$page.props.user.permissions.includes('view-agent-manager')">
                    <span class="menu-header-text">Agents</span>
                </li>
                <li class="menu-item" :class="{ 'active open': $page.component.startsWith('Agents') }"
                    v-if="$page.props.user.permissions.includes('view-agent-manager')">
                    <a href="javascript:void(0);" class="menu-link menu-toggle">
                        <i class="menu-icon tf-icons bx bx-id-card"></i>
                        <div>Agent Manager</div>
                    </a>
                    <ul class="menu-sub">
                        <li class="menu-item" :class="{ 'active': $page.component.startsWith('Agents') }">
                            <a href="/agents" class="menu-link" preserveScroll>
                                <div>Manage Agents</div>
                            </a>
                        </li>
                    </ul>
                </li>

                <!-- reports -->
                <li class="menu-header small text-uppercase"
                    v-if="$page.props.user.permissions.includes('view-reports')">
                    <span class="menu-header-text">Reports</span>
                </li>

                <li class="menu-item" :class="{ 'active open': $page.component.startsWith('Reports') }"
                    v-if="$page.props.user.permissions.includes('view-reports')">
                    <a href="javascript:void(0);" class="menu-link menu-toggle">
                        <i class="menu-icon tf-icons bx bx-file"></i>
                        <div>Reports</div>
                    </a>
                    <ul class="menu-sub">
                        <li class="menu-item" v-if="$page.props.user.permissions.includes('view-arriv-dept-report')"
                            :class="{ 'active': $page.url.startsWith('/reports/arrdeptlist') || $page.url.startsWith('/reports/arrvdepartSearch') }">
                            <Link href="/reports/arrdeptlist" class="menu-link" preserveScroll>
                            <div>Arrv/Deprt Report</div>
                            </Link>
                        </li>
                        <li class="menu-item" v-if="$page.props.user.permissions.includes('view-production-report')"
                            :class="{ 'active': $page.url.startsWith('/reports/productionReport') || $page.url.startsWith('/reports/searchProduction') }">
                            <Link href="/reports/productionReport" class="menu-link" preserveScroll>
                            <div>Production Report</div>
                            </Link>
                        </li>
                        <li class="menu-item" v-if="$page.props.user.permissions.includes('view-bookings-report')"
                            :class="{ 'active': $page.url.startsWith('/reports/bookingsReport') || $page.url.startsWith('/reports/searchBookings') }">
                            <a href="/reports/bookingsReport" class="menu-link" preserveScroll>
                                <div>Bookings Report</div>
                            </a>
                        </li>
                        <li class="menu-item" v-if="$page.props.user.permissions.includes('view-tax-invoice-report')"
                            :class="{ 'active': $page.url.startsWith('/reports/taxInvoiceReport') || $page.url.startsWith('/reports/searchTaxInvoices') }">
                            <a href="/reports/taxInvoiceReport" class="menu-link" preserveScroll>
                                <div>Tax Invoice Report</div>
                            </a>
                        </li>
                        <li class="menu-item" v-if="$page.props.user.permissions.includes('view-profit-loss-report')">
                            <a href="cards-basic.html" class="menu-link">
                                <div>Profit & Loss Report</div>
                            </a>
                        </li>
                    </ul>
                </li>

                <!-- access control -->
                <li class="menu-header small text-uppercase"
                    v-if="$page.props.user.permissions.includes('view-user-manager')">
                    <span class="menu-header-text">Access Control</span>
                </li>
                <li class="menu-item"
                    :class="{ 'active open': $page.component.startsWith('Users') || $page.component.startsWith('Roles') || $page.component.startsWith('Permissions') }"
                    v-if="$page.props.user.permissions.includes('view-user-manager')">
                    <a href="javascript:void(0);" class="menu-link menu-toggle">
                        <i class="menu-icon tf-icons bx bx-user"></i>
                        <div>User Manager</div>
                    </a>
                    <ul class="menu-sub">
                        <li class="menu-item" :class="{ 'active': $page.component.startsWith('Users') }">
                            <Link href="/users" preserve-state preserveScroll class="menu-link">
                            <div>Manage Users</div>
                            </Link>
                        </li>
                        <li class="menu-item" :class="{ 'active': $page.component.startsWith('Roles') }"
                            v-if="$page.props.user.permissions.includes('view-roles')">
                            <Link href="/roles" preserve-state preserveScroll class="menu-link">
                            <div>Manage Roles</div>
                            </Link>
                        </li>
                        <li class="menu-item" :class="{ 'active': $page.component.startsWith('Permissions') }"
                            v-if="$page.props.user.permissions.includes('view-permissions')">
                            <Link href="/permissions" preserve-state preserveScroll class="menu-link">
                            <div>Manage Permissions</div>
                            </Link>
                        </li>
                    </ul>
                </li>
                <!-- settings -->
                <li class="menu-header small text-uppercase"
                    v-if="$page.props.user.permissions.includes('view-settings')">
                    <span class="menu-header-text">System</span>
                </li>
                <li class="menu-item" v-if="$page.props.user.permissions.includes('view-settings')">
                    <a href="javascript:void(0);" class="menu-link menu-toggle">
                        <i class="menu-icon tf-icons bx bx-cog"></i>
                        <div>Settings</div>
                    </a>
                    <ul class="menu-sub">
                        <li class="menu-item">
                            <a href="charts-apex.html" class="menu-link">
                                <div>Access Logs</div>
                            </a>
                        </li>
                        <li class="menu-item">
                            <a href="charts-chartjs.html" class="menu-link">
                                <div>Error Logs</div>
                            </a>
                        </li>
                        <li class="menu-item">
                            <a href="charts-chartjs.html" class="menu-link">
                                <div>Database Dump</div>
                            </a>
                        </li>
                    </ul>
                </li>
            </PerfectScrollbar>
        </ul>
    </aside>
</template>